import { callLambda, type LambdaResultAction } from './lambda.js';
import type { Definition, FieldValues, GrantLevel, TypeUi } from '../models/company-data.js';

// The lambda's first action, with whatever it adds (an id always, a typeId or revision for some)
async function call<T = object>(
  path: string,
  token: string | undefined,
  body: unknown,
): Promise<LambdaResultAction & T> {
  const result = await callLambda(path, 'POST', token, JSON.stringify(body));
  const { code, message } = result.res;
  if (code !== 200) throw new Error(typeof message === 'string' ? message : 'Something went wrong. Please try again.');
  return (Array.isArray(message) ? message[0] : message) as LambdaResultAction & T;
}

export interface TypeInput {
  key?: string;
  typeId?: string;
  title: string;
  description?: string;
  definition: Definition;
  ui: TypeUi;
}

export const saveDataType = (token: string | undefined, type: TypeInput) =>
  call<{ typeId: string; version: string }>('company-data/type/save', token, type);

export interface SetInput {
  setId?: string;
  typeId?: string;
  name: string;
  description?: string;
  editorsTeamId: string | null;
  readers: 'COMPANY' | 'EDITORS';
  privateFieldKeys: string[];
}

export const saveDataSet = (token: string | undefined, set: SetInput) => call('company-data/set/save', token, set);

export const saveDataRecord = (
  token: string | undefined,
  record: { setId?: string; jobId?: string; recordId?: string; revision?: number; fields: FieldValues },
) => call<{ revision: number }>('company-data/record/save', token, record);

// What an open job lets its holder see: only the fields the workflow maps, through the lambda rather than the policy
export interface JobData {
  level: 'READ' | 'EDIT';
  set: { id: string; name: string };
  definition: Definition;
  ui: { titleField: string | null; fields: TypeUi['fields'] };
  records: { id: string; title: string; revision: number; values: FieldValues }[];
  total: number;
  page: number;
  pageSize: number;
}

export interface JobDataQuery {
  recordId?: string;
  // The set the job's references pick from, rather than its own
  linked?: boolean;
  search?: string;
  page?: number;
}

export const readJobData = (token: string | undefined, jobId: string, query: JobDataQuery = {}) =>
  call<JobData>('company-data/job/read', token, { jobId, ...query });

// shortcut: a reference picks from the linked set's first 200 records by title, as the Data area does; swap for a searching picker once sets get large
const LINKED_RECORD_LIMIT = 200;

// The records of the set a job's references pick from, read a page at a time through the job's access
export async function readLinkedRecords(token: string | undefined, jobId: string) {
  const records: JobData['records'] = [];
  let setId = '';
  for (let page = 1; records.length < LINKED_RECORD_LIMIT; page += 1) {
    const data = await readJobData(token, jobId, { linked: true, page });
    setId = data.set.id;
    records.push(...data.records);
    if (records.length >= data.total || data.records.length === 0) break;
  }
  return { setId, records };
}

export const deleteDataRecord = (token: string | undefined, recordId: string) =>
  call('company-data/record/delete', token, { recordId });

export interface OfferInput {
  setId: string;
  recordId?: string;
  granteeCompanyId: string;
  relationshipId: string;
  level: GrantLevel;
  note?: string;
}

export const offerData = (
  token: string | undefined,
  offer: OfferInput | { replacesGrantId: string; level: GrantLevel },
) => call('company-data/grant/offer', token, offer);

export interface RequestInput {
  grantorCompanyId: string;
  relationshipId: string;
  level: GrantLevel;
  note: string;
}

export const requestDataAccess = (
  token: string | undefined,
  request: RequestInput | { replacesGrantId: string; level: GrantLevel; note?: string },
) => call('company-data/grant/request', token, request);

export const answerDataGrant = (
  token: string | undefined,
  answer: { grantId: string; decision: 'accept' | 'decline'; setId?: string; recordId?: string; level?: GrantLevel },
) => call('company-data/grant/respond', token, answer);

export const withdrawDataGrant = (token: string | undefined, grantId: string) =>
  call('company-data/grant/withdraw', token, { grantId });

export const replaceDataGrant = (token: string | undefined, grantId: string, level: GrantLevel) =>
  call('company-data/grant/replace', token, { grantId, level });

export const revokeDataGrant = (token: string | undefined, grantId: string) =>
  call('company-data/grant/revoke', token, { grantId });

export const assignRole = (token: string | undefined, personId: string, roleId: string) =>
  call('role/assign', token, { personId, roleId });

export const unassignRole = (token: string | undefined, personId: string, roleId: string) =>
  call('role/unassign', token, { personId, roleId });

export interface RoleInput {
  roleId?: string;
  name: string;
  description: string;
  permissions: string[];
}

export const saveRole = (token: string | undefined, role: RoleInput) => call('role/save', token, role);

export const deleteRole = (token: string | undefined, roleId: string) => call('role/delete', token, { roleId });

export interface TeamInput {
  teamId?: string;
  name: string;
  type: 'OPERATIONAL' | 'GOVERNANCE';
  memberIds: string[];
}

export const saveTeam = (token: string | undefined, team: TeamInput) => call('company/team/save', token, team);

export interface CompanyProfileInput {
  shortDescription?: string;
  description?: string;
  website?: string;
  contactEmail?: string;
  contactPhone?: string;
}

// The company profile details are written by the server, behind COMPANY_PROFILE_EDIT (#12 D30)
export const saveCompanyProfile = (token: string | undefined, companyId: string, profile: CompanyProfileInput) =>
  call('company/profile/save', token, { companyId, profile });

export interface FeaturesStopped {
  workflowsSwitchedOff: number;
  triggersPaused: number;
  grantsEnded: number;
}

// nodeStream turns a company's Data Studio and Workflow Studio on and off (#12 D16, D37)
export const setCompanyFeatures = (token: string | undefined, companyId: string, features: string[]) =>
  call<FeaturesStopped>('platform/company/features', token, { companyId, features });

export const deleteTeam = (token: string | undefined, teamId: string) => call('company/team/delete', token, { teamId });

export type PlatformRoleKey = 'NS_ADMIN' | 'NS_STAFF';

export const assignPlatformRole = (token: string | undefined, personId: string, systemKey: PlatformRoleKey) =>
  call('platform/role/assign', token, { personId, systemKey });

export const unassignPlatformRole = (token: string | undefined, personId: string, systemKey: PlatformRoleKey) =>
  call('platform/role/unassign', token, { personId, systemKey });

export const setPlatformTeamMembers = (token: string | undefined, teamId: string, memberIds: string[]) =>
  call('platform/team/members', token, { teamId, memberIds });
